import { useRef, useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Mic, ChevronDown, Download, Loader2, Play, Pause, Trash2 } from 'lucide-react';
import { C } from '../styles/tokens';
import { AudioInput } from '../components/audio-input';
import useIsMobile from '../hooks/useIsMobile';
import EmbeddedVoicePicker from '../components/voice/EmbeddedVoicePicker';
import { useLanguages } from '../http-hooks/ai-voices/useLanguages';
import {
    useVoiceChanges,
    useCreateVoiceChange,
    useDeleteVoiceChange,
    fetchChangerPresignedUrl,
    fetchVoiceChangeDownloadUrl,
    uploadToS3,
} from '../http-hooks/ai-voices/useVoiceChanger';
import { downloadFile } from '../utils/downloadFile';
import GenerationsTable from '../components/media/GenerationsTable';
import ConfirmDialog from '../components/media/ConfirmDialog';
import FeatureCreditCost from '../components/plans/FeatureCreditCost';
import { useFeatureCreditGate } from '../http-hooks/organisation/useFeatureCreditEstimate';
import { toast } from 'react-toastify';
import { AUDIO_EXTENSIONS, getExtension, stripExtension, formatDuration } from '../utils/audioFile';
import { addMixPanelEvent } from '../store/actions';

const AVATAR_COLORS = [
    { bg: '#EDE9FE', color: '#6D28D9' },
    { bg: '#FEF3C7', color: '#92400E' },
    { bg: '#D1FAE5', color: '#065F46' },
    { bg: '#DBEAFE', color: '#1E40AF' },
    { bg: '#FCE7F3', color: '#831843' },
    { bg: '#FEE2E2', color: '#991B1B' },
    { bg: '#CCFBF1', color: '#0F766E' },
    { bg: '#FFF7ED', color: '#C2410C' },
];

function voiceAvatarColor(name = '') {
    const code = name.split('').reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

// ── Settings ─────────────────────────────────────────────────────────────────
function SettingsSlider({ label, leftLabel, rightLabel, value, onChange }) {
    return (
        <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                <span style={{ fontSize: 12.5, fontWeight: 600, color: C.n700 }}>{label}</span>
                <span style={{ fontSize: 12, color: C.n400, fontVariantNumeric: 'tabular-nums' }}>{value}%</span>
            </div>
            <input
                type="range"
                min={0}
                max={100}
                value={value}
                onChange={e => onChange(Number(e.target.value))}
                className="vc-slider"
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2, fontSize: 10.5, color: C.n400 }}>
                <span>{leftLabel}</span>
                <span>{rightLabel}</span>
            </div>
        </div>
    );
}

// Segmented "Voice only" / "Voice + background" switch — replaces the old on/off toggle.
function NoiseSegmentedControl({ removeNoise, onChange }) {
    return (
        <div>
            <div style={{ fontSize: 11, fontWeight: 600, color: C.n400, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 8 }}>
                Background noise
            </div>
            <div style={{
                display: 'inline-flex', background: C.w, border: `1px solid ${C.n200}`,
                borderRadius: 9, padding: 3, gap: 2,
            }}>
                <button
                    type="button"
                    onClick={() => onChange(true)}
                    style={{
                        padding: '7px 14px', borderRadius: 7, border: 'none',
                        background: removeNoise ? '#2563EB' : 'transparent',
                        color: removeNoise ? C.w : C.n600,
                        fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                        transition: 'background .15s, color .15s',
                    }}
                >
                    Voice only
                </button>
                <button
                    type="button"
                    onClick={() => onChange(false)}
                    style={{
                        padding: '7px 14px', borderRadius: 7, border: 'none',
                        background: !removeNoise ? '#2563EB' : 'transparent',
                        color: !removeNoise ? C.w : C.n600,
                        fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
                        transition: 'background .15s, color .15s',
                    }}
                >
                    Voice + background
                </button>
            </div>
            <p style={{ fontSize: 11.5, color: C.n500, marginTop: 8, marginBottom: 0 }}>
                {removeNoise
                    ? 'Input is routed through the denoiser first — only the clean voice is transformed.'
                    : 'Background sounds are preserved alongside the changed voice.'}
            </p>
        </div>
    );
}

// ── Shared voice-changer settings state ────────────────────────────────────────
function useVoiceSettings() {
    const [selectedVoice, setSelectedVoice] = useState(null);
    const [stability, setStability] = useState(50);
    const [similarity, setSimilarity] = useState(65);
    const [removeNoise, setRemoveNoise] = useState(false);

    const { data: languagesData } = useLanguages();
    const defaultVoice = useMemo(() => {
        const voices = languagesData?.['en-US']?.voices || [];
        const first = voices.find(v => v.engine === 'emotional' && v.gender === 'female');
        return first ? { ...first, langCode: 'en-US' } : null;
    }, [languagesData]);

    // User's manual pick takes precedence; fall back to computed default
    const effectiveVoice = selectedVoice ?? defaultVoice;

    return {
        selectedVoice, setSelectedVoice, effectiveVoice,
        stability, setStability,
        similarity, setSimilarity,
        removeNoise, setRemoveNoise,
    };
}

// ── Voice picker — dropdown trigger, popover content is the real EmbeddedVoicePicker ──
function VoiceDropdown({ voice, onSelect }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const avatarColor = voice ? voiceAvatarColor(voice.name) : null;

    useEffect(() => {
        const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                    padding: '10px 12px', borderRadius: 10,
                    border: `1px solid ${open ? C.p600 : C.n200}`, background: C.w,
                    cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                    transition: 'border-color .12s',
                }}
            >
                {voice ? (
                    <div style={{
                        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                        background: avatarColor.bg, color: avatarColor.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 13, fontWeight: 700,
                    }}>
                        {voice.name[0]}
                    </div>
                ) : (
                    <div style={{
                        width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                        background: C.n100, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <Mic size={13} color={C.n400} />
                    </div>
                )}
                <span style={{
                    flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500,
                    color: voice ? C.n800 : C.n400,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {voice ? voice.name : 'Select a voice'}
                </span>
                <ChevronDown size={14} color={C.n400} style={{ flexShrink: 0, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
            </button>

            {open && (
                <div style={{
                    position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 10,
                    background: C.w, border: `1px solid ${C.n200}`, borderRadius: 14,
                    boxShadow: '0 12px 32px rgba(15,23,42,.14)', overflow: 'hidden',
                    height: 420, display: 'flex', flexDirection: 'column',
                }}>
                    <EmbeddedVoicePicker
                        emotionalOnly
                        selectedVoice={voice}
                        onVoiceSelect={v => { onSelect(v); setOpen(false); }}
                    />
                </div>
            )}
        </div>
    );
}

// ── Generations row ────────────────────────────────────────────────────────────
function ChangeRow({ item, org_id, integration_id, playingId, setPlayingId, gridTemplateColumns, isMobile }) {
    const [downloading, setDownloading] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const [loadingAudio, setLoadingAudio] = useState(false);
    const [delHover, setDelHover] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const audioRef = useRef(null);
    const audioUrlRef = useRef(null); // cached signed URL, fetched lazily on first play
    const deleteMutation = useDeleteVoiceChange();
    const isReady = item.status === 'ready';
    const isFailed = item.status === 'failed';

    // Only one row plays at a time — when another row becomes active, stop ours.
    useEffect(() => {
        if (playingId !== item.id && isPlaying) audioRef.current?.pause();
    }, [playingId, item.id, isPlaying]);

    const resolveOutputUrl = async () => {
        if (audioUrlRef.current) return audioUrlRef.current;
        const { url } = await fetchVoiceChangeDownloadUrl(org_id, integration_id, item.id);
        audioUrlRef.current = url;
        return url;
    };

    const togglePlay = async () => {
        if (!isReady || loadingAudio) return;
        const audio = audioRef.current;
        if (!audio) return;
        if (isPlaying) { audio.pause(); setIsPlaying(false); setPlayingId(null); return; }
        try {
            setLoadingAudio(true);
            const url = await resolveOutputUrl();
            if (audio.src !== url) audio.src = url;
            await audio.play();
            setIsPlaying(true);
            setPlayingId(item.id);
        } catch (e) {
            console.error('Playback failed', e);
        } finally {
            setLoadingAudio(false);
        }
    };

    const handleDownload = async () => {
        if (!isReady || downloading) return;
        setDownloading(true);
        try {
            const url = await resolveOutputUrl();
            await downloadFile(url, `${item.name}-changed.${item.output_format || 'wav'}`);
        } catch (e) {
            console.error('Download failed', e);
        } finally {
            setDownloading(false);
        }
    };

    const handleDelete = async () => {
        if (deleteMutation.isLoading) return;
        setConfirmOpen(false);
        try {
            if (isPlaying) { audioRef.current?.pause(); setPlayingId(null); }
            await deleteMutation.mutateAsync({ org_id, integration_id, change_id: item.id });
            toast.success('Conversion deleted');
        } catch (e) {
            console.error('Delete failed', e);
            toast.error('Failed to delete. Please try again.');
        }
    };

    const playButton = (
        <button
            type="button"
            onClick={togglePlay}
            disabled={!isReady || loadingAudio}
            title={isReady ? (isPlaying ? 'Pause' : 'Play') : undefined}
            style={{
                width: 32, height: 32, borderRadius: 9, flexShrink: 0, border: 'none', padding: 0,
                background: C.n100, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: isReady && !loadingAudio ? 'pointer' : 'default',
            }}
        >
            {item.status === 'processing' || loadingAudio
                ? <Loader2 size={15} color={C.n500} className="vc-spin" />
                : isFailed
                    ? <Mic size={14} color="#EF4444" />
                    : isPlaying
                        ? <Pause size={14} color={C.n700} fill={C.n700} />
                        : <Play size={14} color={C.n700} fill={C.n700} style={{ marginLeft: 1 }} />}
        </button>
    );

    const statusNode = item.status === 'processing' ? 'Processing…' : isFailed ? <span style={{ color: '#EF4444' }}>Failed</span> : formatDuration(item.audio_duration);

    const downloadButton = (
        <button
            type="button"
            disabled={!isReady || downloading}
            onClick={handleDownload}
            title="Download converted audio"
            style={{
                width: 26, height: 26, border: 'none', background: 'transparent', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: isReady && !downloading ? 'pointer' : 'default',
            }}
        >
            {downloading
                ? <Loader2 size={15} color={C.n400} className="vc-spin" />
                : <Download size={15} color={C.n400} />}
        </button>
    );

    const deleteButton = (
        <button
            type="button"
            disabled={deleteMutation.isLoading}
            onClick={() => setConfirmOpen(true)}
            onMouseEnter={() => setDelHover(true)}
            onMouseLeave={() => setDelHover(false)}
            title="Delete conversion"
            style={{
                width: 26, height: 26, border: 'none', borderRadius: 7, flexShrink: 0,
                background: delHover ? '#FEE2E2' : 'transparent',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: deleteMutation.isLoading ? 'default' : 'pointer',
            }}
        >
            {deleteMutation.isLoading
                ? <Loader2 size={15} color={C.n400} className="vc-spin" />
                : <Trash2 size={15} color={delHover ? '#DC2626' : C.n400} />}
        </button>
    );

    const confirmDialog = (
        <ConfirmDialog
            open={confirmOpen}
            title="Delete conversion"
            message={`Delete "${item.name}"? This can't be undone.`}
            onConfirm={handleDelete}
            onCancel={() => setConfirmOpen(false)}
        />
    );

    const audioEl = <audio ref={audioRef} onEnded={() => setIsPlaying(false)} onPause={() => setIsPlaying(false)} />;

    if (isMobile) {
        return (
            <div style={{
                display: 'flex', flexDirection: 'column', gap: 8,
                padding: '12px 16px', borderTop: `1px solid ${C.n100}`,
                opacity: isReady ? 1 : 0.55,
            }}>
                {audioEl}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {playButton}
                    <span style={{
                        minWidth: 0, flex: 1, fontSize: 13.5, fontWeight: 600, color: isReady ? C.n900 : C.n400,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                        {item.name}
                    </span>
                    <span style={{ fontSize: 12.5, color: C.n500, flexShrink: 0, whiteSpace: 'nowrap' }}>
                        {statusNode}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        {downloadButton}
                        {deleteButton}
                    </div>
                </div>
                {confirmDialog}
            </div>
        );
    }

    return (
        <div style={{
            display: 'grid', gridTemplateColumns, gap: 12, alignItems: 'center',
            padding: '12px 20px', borderTop: `1px solid ${C.n100}`,
            opacity: isReady ? 1 : 0.55,
        }}>
            {audioEl}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                {playButton}
                <span style={{
                    minWidth: 0, fontSize: 13.5, fontWeight: 600, color: isReady ? C.n900 : C.n400,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    {item.name}
                </span>
            </div>
            <div style={{ fontSize: 12.5, color: C.n500, textAlign: 'center' }}>
                {statusNode}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                {downloadButton}
                {deleteButton}
            </div>
            {confirmDialog}
        </div>
    );
}

function ChangerGenerations({ org_id, integration_id }) {
    const [playingId, setPlayingId] = useState(null);
    const { data = [], isLoading } = useVoiceChanges({ org_id, integration_id });

    return (
        <GenerationsTable
            title="Generations"
            count={data.length}
            columns={[
                { key: 'duration', label: 'Duration', width: '110px' },
            ]}
            items={data}
            rowKey={item => item.id}
            renderRow={(item, gridTemplateColumns, key, isMobile) => (
                <ChangeRow
                    key={key}
                    item={item}
                    org_id={org_id}
                    integration_id={integration_id}
                    playingId={playingId}
                    setPlayingId={setPlayingId}
                    gridTemplateColumns={gridTemplateColumns}
                    isMobile={isMobile}
                />
            )}
            loading={isLoading}
            emptyIcon={<Mic size={24} color={C.n300} />}
            emptyTitle="No history yet"
            emptySubtitle="Upload or record audio and pick a voice to make your first conversion"
        />
    );
}

// Custom range-slider thumb/track styling injected once
// (pseudo-elements like ::-webkit-slider-thumb can't be targeted with inline styles)
if (typeof document !== 'undefined' && !document.getElementById('vc-styles')) {
    const s = document.createElement('style');
    s.id = 'vc-styles';
    s.textContent = `
        .vc-slider { -webkit-appearance: none; appearance: none; width: 100%; height: 4px; border-radius: 999px; background: ${C.n200}; outline: none; cursor: pointer; margin: 6px 0; }
        .vc-slider::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 18px; height: 18px; border-radius: 50%; background: ${C.p600}; border: none; cursor: pointer; box-shadow: 0 1px 3px rgba(0,0,0,.25); }
        .vc-slider::-moz-range-thumb { width: 18px; height: 18px; border-radius: 50%; background: ${C.p600}; border: none; cursor: pointer; box-shadow: 0 1px 3px rgba(0,0,0,.25); }
        .vc-slider::-moz-range-track { height: 4px; border-radius: 999px; background: ${C.n200}; }
    `;
    document.head.appendChild(s);
}

export default function VoiceChanger() {
    const { org_id, id: integration_id } = useParams();
    const isMobile = useIsMobile();
    const settings = useVoiceSettings();
    const { effectiveVoice, setSelectedVoice } = settings;
    const dispatch = useDispatch();

    const [audioValue, setAudioValue] = useState(null);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const createChange = useCreateVoiceChange();

    const handleSubmit = async () => {
        if (!audioValue || submitting) return;
        if (!effectiveVoice?.audio) { setError('Select a target voice first.'); return; }
        const file = audioValue.file;
        setError(null);
        setSubmitting(true);
        try {
            const ext = getExtension(file.name) || 'wav';
            const contentType = file.type && file.type.startsWith('audio/') ? file.type : 'audio/mpeg';

            const { signed_url, url } = await fetchChangerPresignedUrl({
                org_id, integration_id, fileName: file.name, contentType,
            });
            await uploadToS3(signed_url, file);

            dispatch(addMixPanelEvent({
                orgId: org_id, integrationId: integration_id, body: {
                    eventName: "VOICE_CHANGER_STARTED",
                    eventData: {
                        tool: "voice_changer", // GROW-84: tool on every stage for start->finish comparison
                        source_audio_path: url,
                        source_format: ext,
                        audio_duration: audioValue.durationSec != null ? Math.round(audioValue.durationSec) : undefined,
                        target_voice_name: effectiveVoice.name,
                        target_voice_code: effectiveVoice.code,
                        target_voice_audio: effectiveVoice.audio,
                        lang_code: effectiveVoice.langCode,
                        settings: {
                            stability: settings.stability,
                            similarity: settings.similarity,
                            remove_noise: settings.removeNoise,
                        },
                    }
                }
            }));

            await createChange.mutateAsync({
                org_id, integration_id,
                name: stripExtension(file.name),
                source_audio_path: url,
                source_format: ext,
                audio_duration: audioValue.durationSec != null ? Math.round(audioValue.durationSec) : undefined,
                target_voice_audio: effectiveVoice.audio,
                target_voice_code: effectiveVoice.code,
                target_voice_name: effectiveVoice.name,
                lang_code: effectiveVoice.langCode,
                settings: {
                    stability: settings.stability,
                    similarity: settings.similarity,
                    remove_noise: settings.removeNoise,
                },
            });

            setAudioValue(null);
        } catch (e) {
            setError(e?.message || 'Failed to start voice change. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const canSubmit = !!audioValue && !!effectiveVoice?.audio;

    const { insufficientCredits: hasInsufficientCredits } = useFeatureCreditGate({
        org_id, feature: 'voice-changer',
        duration_seconds: audioValue?.durationSec != null ? Math.round(audioValue.durationSec) : null,
        enabled: canSubmit,
    });

    return (
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <h1 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 800, letterSpacing: '-.02em', color: C.n900, margin: '0 0 20px' }}>
                Voice Changer
            </h1>

            <div style={{
                background: C.w, border: `1px solid ${C.n200}`, borderRadius: 16,
                padding: 20, boxShadow: '0 4px 20px rgba(15,23,42,.06)',
            }}>
                <AudioInput
                    compact
                    value={audioValue}
                    onChange={setAudioValue}
                    onError={e => setError(e.message)}
                    renderError={() => null}
                    validation={{ maxSizeBytes: 50 * 1024 * 1024, maxDurationSec: 600, allowedExtensions: AUDIO_EXTENSIONS }}
                    recording={{ micDeviceSelector: true, maxRecordingDurationSec: 600 }}
                />

                {error && (
                    <div style={{ marginTop: 12, fontSize: 13, color: '#EF4444', fontWeight: 500 }}>{error}</div>
                )}

                <div style={{ marginTop: 20 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.n400, letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 8 }}>
                        Voice
                    </div>
                    <VoiceDropdown voice={effectiveVoice} onSelect={setSelectedVoice} />
                </div>

                <div style={{
                    marginTop: 20, background: C.n50, border: `1px solid ${C.n100}`,
                    borderRadius: 12, padding: '14px 16px',
                }}>
                    {/* Hidden per request — keeping the sliders/state around in case they come back.
                    <div style={{
                        display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', columnGap: 28,
                    }}>
                        <SettingsSlider
                            label="Stability"
                            leftLabel="More variable"
                            rightLabel="More stable"
                            value={settings.stability}
                            onChange={settings.setStability}
                        />
                        <SettingsSlider
                            label="Similarity"
                            leftLabel="Low"
                            rightLabel="High"
                            value={settings.similarity}
                            onChange={settings.setSimilarity}
                        />
                    </div>
                    <div style={{ height: 1, background: C.n200, margin: '2px 0 12px' }} />
                    */}
                    <NoiseSegmentedControl
                        removeNoise={settings.removeNoise}
                        onChange={settings.setRemoveNoise}
                    />
                </div>

                <div style={{ marginTop: 20, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
                    <FeatureCreditCost
                        orgId={org_id}
                        feature="voice-changer"
                        durationSeconds={audioValue?.durationSec != null ? Math.round(audioValue.durationSec) : null}
                        enabled={canSubmit}
                    />
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!canSubmit || submitting || hasInsufficientCredits}
                        title={hasInsufficientCredits ? 'Not enough credits to run the voice changer' : ''}
                        style={{
                            padding: '10px 22px', borderRadius: 10, border: 'none',
                            background: canSubmit && !submitting && !hasInsufficientCredits ? C.n900 : C.n200,
                            color: canSubmit && !submitting && !hasInsufficientCredits ? C.w : C.n400,
                            fontSize: 13.5, fontWeight: 700,
                            cursor: canSubmit && !submitting && !hasInsufficientCredits ? 'pointer' : 'not-allowed',
                            display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'inherit',
                        }}
                    >
                        {submitting && <Loader2 size={15} color={C.w} className="vc-spin" />}
                        {submitting ? 'Generating…' : 'Generate speech'}
                    </button>
                </div>
            </div>

            <ChangerGenerations org_id={org_id} integration_id={integration_id} />
        </div>
    );
}
